import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { HttpErrorResponse } from '@angular/common/http';
import { MkgError } from '../class/mkg-error';
import { UserUtilities } from '@app/class/user-utilities';
import { environment } from '@env/environment';

interface MkgLog {
  timestamp: number;
  caller: string;
  info?: string;
  text?: string;
  error?: string;
}

interface LogSerie {
  id: number;
  companyId: string,
  companyCNPJ: string,
  loggedUserId: string,
  loggedUserEmail: string,
  userRoleInCompany: number,
  SO: string,
  browser: string,
  envName: string,
  companyState: number,
  logs: MkgLog[]
}

export enum LOG_REPORTER_KEYS {
  CASHIER = "cashier-logs",
  STOCK = "stock-logs",
  INVOICE = "mkg-logs",
}

/** Legacy browser-storage keys that previously held persisted logs. Purged on startup; never written. */
const LEGACY_LOG_KEYS = ["mkg-logs", "cashier-logs", "stock-logs", "unknown", "logs"];

@Injectable({
  providedIn: 'root'
})
export class LogReporterService {
  private _currentId: number;
  private _currentSeriesMap = new Map<LOG_REPORTER_KEYS, LogSerie>();

  constructor(
    private _dataService: DataService
  ) {
    this._currentId = Date.now();

    // Logs are no longer persisted. Purge any values still stored in users' browsers from previous versions.
    for (const key of LEGACY_LOG_KEYS) {
      localStorage.removeItem(key);
    }

    _dataService.ready$.subscribe(([user, company]) => {
      for (const key of [LOG_REPORTER_KEYS.CASHIER, LOG_REPORTER_KEYS.INVOICE, LOG_REPORTER_KEYS.STOCK]) {
        const serie = this._currentSeriesMap.get(key);
        let logs = serie?.logs || [];
        if (serie && serie.companyCNPJ !== company.cnpj) {
          logs.push({
            caller: "LogReporterService",
            timestamp: Date.now(),
            info: `Mudou de "${serie.companyCNPJ}" para "${company.cnpj}"`
          })
        }
        this._currentSeriesMap.set(key, {
          id: this._currentId,
          SO: UserUtilities.getInfosSO(),
          browser: UserUtilities.getInfosBrowser(),
          envName: environment.envName,
          companyState: company.currentState,
          companyId: company.id,
          companyCNPJ: company.cnpj,
          loggedUserEmail: user.email,
          loggedUserId: user.id,
          userRoleInCompany: user.getRoleIn(company),
          logs
        });
      }
    });
  }

  errorLogger(caller: string, key?: LOG_REPORTER_KEYS) {
    return (error: MkgError | any) => {
      try {
        const log: MkgLog = {
          caller,
          timestamp: Date.now(),
          error: JSON.stringify({
            stack: error.stack,
            class: error.constructor?.name,
            cause: error.cause,
            name: (error as HttpErrorResponse).name,
            message: (error as HttpErrorResponse).message,
            error: (error as HttpErrorResponse).error,
            status: (error as HttpErrorResponse).status,
            statusText: (error as HttpErrorResponse).statusText,
            type: (error as HttpErrorResponse).type,
            ok: (error as HttpErrorResponse).ok,
            url: (error as HttpErrorResponse).url,
            body: (error as MkgError).body,
            method: (error as MkgError).method,
            headers: (error as MkgError).rawHeaders,
            attemps: (error as MkgError).attempts,
            reason: error?.reason,
          })
        }
        this._appendLog(log, key)
      } catch (error) {
        console.error("Erro ao adicionar log de erro ao relatório", error)
      }
    }
  }

  textLogger(caller: string, key?: LOG_REPORTER_KEYS) {
    return (text: string) => {
      try {
        const log: MkgLog = {
          caller,
          text,
          timestamp: Date.now()
        }
        this._appendLog(log, key)
      } catch (error) {
        console.error("Erro ao adicionar log de texto ao relatório", error)
      }
    }
  }

  private _appendLog(log: MkgLog, key?: LOG_REPORTER_KEYS) {
    // In-memory / console only. Logs are no longer persisted to localStorage.
    if (key && this._currentSeriesMap.has(key)) {
      const serie = this._currentSeriesMap.get(key)
      serie.logs.push(log);
      this._currentSeriesMap.set(key, serie);
    } else {
      console.log(log);
    }
  }

}
